import { useEffect, useState } from 'react';
import { loadRecon, markInvoicePaid, type ReconRow } from '../../lib/qontoService';

// Panneau super admin — Rapprochement Qonto ↔ Odoo : Facturé vs Encaissé par mois/resto.

const eur = (cents: number) => `${(cents / 100).toFixed(2)} €`;

const RECON: Record<string, { label: string; cls: string }> = {
  reconciled: { label: '✓ Rapproché', cls: 'bg-emerald-100 text-emerald-700' },
  ecart: { label: '⚠ Écart', cls: 'bg-amber-100 text-amber-700' },
  not_collected: { label: 'Non encaissé', cls: 'bg-slate-100 text-slate-600' },
  pending: { label: 'En cours', cls: 'bg-blue-100 text-blue-700' },
  failed: { label: 'Échec', cls: 'bg-red-100 text-red-700' },
  no_invoice: { label: 'Sans facture', cls: 'bg-slate-100 text-slate-500' },
};

/** Mois courant et 5 précédents au format YYYY-MM. */
function monthOptions(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    d.setUTCMonth(d.getUTCMonth() - 1);
  }
  return out;
}

export default function QontoReconPanel() {
  const months = monthOptions();
  // Par défaut : mois précédent (les factures/prélèvements du mois écoulé).
  const [tag, setTag] = useState(months[1] ?? months[0]);
  const [rows, setRows] = useState<ReconRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [rowMsg, setRowMsg] = useState<Record<string, string>>({});

  const load = (t: string) => {
    setLoading(true);
    setErr('');
    loadRecon(t).then((res) => {
      setRows(res.ok ? res.rows : []);
      if (!res.ok) setErr(res.error ?? 'Erreur.');
      setLoading(false);
    });
  };

  useEffect(() => { load(tag); }, [tag]);

  const pay = async (slug: string) => {
    setBusy(slug);
    const res = await markInvoicePaid(slug, tag);
    setBusy('');
    setRowMsg((m) => ({ ...m, [slug]: res.ok ? (res.message ?? '✅ Payée.') : (res.error ?? 'Échec.') }));
    if (res.ok) load(tag);
  };

  const totals = rows.reduce(
    (a, r) => ({ inv: a.inv + r.invoiced_cents, col: a.col + r.collected_cents }),
    { inv: 0, col: 0 },
  );

  return (
    <div className="max-w-5xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-slate-900">🔗 Rapprochement Qonto ↔ Odoo</h2>
          <p className="text-sm text-slate-500">Facturé (Odoo) vs Encaissé (Qonto SDD), par mois.</p>
        </div>
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
        >
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500">Total facturé</div>
          <div className="text-2xl font-black text-slate-900">{eur(totals.inv)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500">Total encaissé</div>
          <div className="text-2xl font-black text-emerald-700">{eur(totals.col)}</div>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {err.includes('qonto_monthly_collects') || err.includes('does not exist')
            ? 'Base non à jour — exécute sql/45_qonto_monthly_collect.sql.'
            : err}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        {loading ? (
          <p className="opacity-70">Chargement…</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                <th className="px-2 py-2">Restaurant</th>
                <th className="px-2 py-2 text-right">Facturé</th>
                <th className="px-2 py-2 text-right">Encaissé</th>
                <th className="px-2 py-2 text-center">Rapprochement</th>
                <th className="px-2 py-2 text-right">Odoo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = RECON[r.recon] ?? RECON.pending;
                const canPay = r.recon === 'reconciled' && r.payment_state !== 'paid';
                return (
                  <tr key={r.slug} className="border-b border-slate-100">
                    <td className="px-2 py-2 font-semibold text-slate-700">{r.name}</td>
                    <td className="px-2 py-2 text-right text-slate-700">{eur(r.invoiced_cents)}</td>
                    <td className="px-2 py-2 text-right text-emerald-700">{eur(r.collected_cents)}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.payment_state === 'paid' ? (
                        <span className="text-xs font-semibold text-emerald-600">payée</span>
                      ) : canPay ? (
                        <button
                          onClick={() => pay(r.slug)}
                          disabled={busy === r.slug}
                          className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                        >
                          {busy === r.slug ? '…' : 'Marquer payée'}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                      {rowMsg[r.slug] && <div className="mt-1 text-[11px] text-slate-500">{rowMsg[r.slug]}</div>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-400">Aucune ligne pour {tag}.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
